import { useCallback, useEffect, useState } from 'react';
import { type HBar, HBars } from '../components/charts.js';
import { Badge, ErrorNote, type Notify, Panel, timeAgo } from '../components/ui.js';
import {
  type EventLevel,
  type SystemEventGroup,
  getSystemEvents,
  reopenSystemEvent,
  resolveSystemEvent,
} from '../lib/internal.js';
import { useAsyncData } from '../lib/useAsyncData.js';

type Tone = 'emerald' | 'sky' | 'amber' | 'rose' | 'neutral';

const LEVEL: Record<EventLevel, { label: string; tone: Tone }> = {
  critical: { label: 'Critical', tone: 'rose' },
  error: { label: 'Error', tone: 'amber' },
  warn: { label: 'Warning', tone: 'sky' },
};

type StatusFilter = 'unresolved' | 'resolved' | 'all';
const REFRESH_MS = 20_000;

export function Errors({ notify }: { notify: Notify }) {
  const [status, setStatus] = useState<StatusFilter>('unresolved');
  const [busy, setBusy] = useState<string | null>(null);
  const { data, error, reload } = useAsyncData(
    useCallback(() => getSystemEvents({ status, limit: 100 }), [status]),
  );

  // Live radar — refresh on an interval so new issues appear without a manual reload.
  useEffect(() => {
    const id = setInterval(() => reload(), REFRESH_MS);
    return () => clearInterval(id);
  }, [reload]);

  const act = async (fn: () => Promise<unknown>, fingerprint: string, label: string) => {
    setBusy(fingerprint);
    try {
      await fn();
      notify(label, 'success');
      reload();
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Action failed', 'error');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      <Panel title="Error radar">
        <p className="mb-4 text-sm text-neutral-500">
          Every error, issue, and degraded outcome detected across ingest, AI / auto-config, account
          setup &amp; provisioning, auth, email, and the cron — grouped so a recurring problem rolls
          up to one row. Resolve one once you’ve handled it; it reappears if it happens again.
        </p>

        <div className="mb-4 flex flex-wrap items-center gap-2">
          {(['unresolved', 'resolved', 'all'] as StatusFilter[]).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              className={`rounded-full px-3 py-1 text-xs font-medium capitalize ${
                status === s
                  ? 'bg-neutral-900 text-white'
                  : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
              }`}
            >
              {s}
            </button>
          ))}
          <button
            type="button"
            onClick={reload}
            className="ml-auto text-xs text-neutral-400 hover:text-neutral-700 hover:underline"
          >
            Refresh
          </button>
        </div>

        {error ? (
          <ErrorNote message={error} onRetry={reload} />
        ) : !data ? (
          <p className="text-sm text-neutral-500">Loading…</p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <Tile
                label="Open issues"
                value={data.summary.groups}
                tone={data.summary.groups > 0 ? 'rose' : 'emerald'}
              />
              <Tile
                label="Critical"
                value={data.summary.bySeverity.critical ?? 0}
                tone={(data.summary.bySeverity.critical ?? 0) > 0 ? 'rose' : 'emerald'}
              />
              <Tile
                label="Events (24h)"
                value={data.summary.last24h}
                tone={data.summary.last24h > 0 ? 'amber' : 'emerald'}
              />
              <Tile label="Occurrences" value={data.summary.total} tone="neutral" />
            </div>

            <div className="mt-5 grid gap-6 border-t border-neutral-100 pt-4 lg:grid-cols-2">
              <div>
                <div className="mb-2 text-xs font-medium uppercase tracking-wide text-neutral-500">
                  By severity
                </div>
                <HBars
                  data={(['critical', 'error', 'warn'] as EventLevel[]).map(
                    (s): HBar => ({
                      label: LEVEL[s].label,
                      value: data.summary.bySeverity[s] ?? 0,
                      tone: LEVEL[s].tone,
                    }),
                  )}
                  empty="No events."
                />
              </div>
              <div>
                <div className="mb-2 text-xs font-medium uppercase tracking-wide text-neutral-500">
                  By source
                </div>
                <HBars
                  data={Object.entries(data.summary.bySource)
                    .sort((a, b) => b[1] - a[1])
                    .map(([k, v]): HBar => ({ label: k, value: v, tone: 'neutral' }))}
                  empty="No events by source."
                />
              </div>
            </div>
            {data.summary.capped && (
              <p className="mt-3 text-xs text-amber-600">
                Showing the most recent {data.summary.total} events (scan cap reached) — resolve
                some to see the rest.
              </p>
            )}
          </>
        )}
      </Panel>

      {data && data.groups.length > 0 && (
        <Panel title={`Issues (${data.groups.length})`}>
          <table className="w-full text-sm">
            <thead className="text-left text-neutral-500">
              <tr>
                <th className="py-1">Severity</th>
                <th>Source</th>
                <th>Issue</th>
                <th className="text-right">Count</th>
                <th className="text-right">Last seen</th>
                <th className="text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {data.groups.map((g: SystemEventGroup) => (
                <tr key={g.fingerprint} className="border-t border-neutral-100 align-top">
                  <td className="py-2">
                    <Badge tone={LEVEL[g.level]?.tone ?? 'neutral'}>
                      {LEVEL[g.level]?.label ?? g.level}
                    </Badge>
                  </td>
                  <td className="py-2 font-mono text-xs text-neutral-600">
                    {g.source}
                    <div className="text-[11px] text-neutral-400">{g.event}</div>
                  </td>
                  <td className="min-w-0 py-2 text-neutral-700">
                    <div className="break-words" title={g.message}>
                      {g.message}
                    </div>
                    {g.accountId && (
                      <div className="text-[11px] text-neutral-400">account {g.accountId}</div>
                    )}
                  </td>
                  <td className="py-2 text-right tabular-nums text-neutral-600">{g.count}</td>
                  <td className="py-2 text-right text-neutral-500">{timeAgo(g.lastSeen)}</td>
                  <td className="py-2 text-right">
                    {g.resolvedAt ? (
                      <button
                        type="button"
                        disabled={busy === g.fingerprint}
                        onClick={() =>
                          act(() => reopenSystemEvent(g.fingerprint), g.fingerprint, 'Reopened')
                        }
                        className="text-xs text-neutral-500 hover:text-neutral-800 hover:underline disabled:opacity-50"
                      >
                        Reopen
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled={busy === g.fingerprint}
                        onClick={() =>
                          act(() => resolveSystemEvent(g.fingerprint), g.fingerprint, 'Resolved')
                        }
                        className="rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                      >
                        Resolve
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      {data && data.groups.length === 0 && (
        <Panel title="Issues">
          <p className="py-6 text-center text-sm text-neutral-500">
            {status === 'unresolved'
              ? 'No open issues — everything is healthy.'
              : 'Nothing to show for this filter.'}
          </p>
        </Panel>
      )}
    </div>
  );
}

/** Compact KPI tile (local — mirrors the Metric look used elsewhere). */
function Tile({ label, value, tone }: { label: string; value: number; tone: Tone }) {
  const ring: Record<Tone, string> = {
    rose: 'text-rose-700',
    amber: 'text-amber-700',
    sky: 'text-sky-700',
    emerald: 'text-emerald-700',
    neutral: 'text-neutral-800',
  };
  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-3">
      <div className="text-xs text-neutral-500">{label}</div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums ${ring[tone]}`}>{value}</div>
    </div>
  );
}
